// FILE: src/pages/Visits/VisitsPage.jsx
// Full visits management — list, schedule, update status, delete
import React, { useEffect, useState, useCallback } from 'react';
import { visitsAPI, clientsAPI, caregiversAPI, servicesAPI } from '../../api/services';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import '../../components/UI/UI.css';
import './Visits.css';

const normalizeDT = (d) => {
  if (!d) return '';
  return String(d).replace('T', ' ').replace('Z', '').slice(0, 16);
};

const toInputDT = (d) => {
  if (!d) return '';
  return normalizeDT(d).replace(' ', 'T');
};

const fmtDate = (d) => {
  const s = normalizeDT(d);
  if (!s) return '—';

  const [datePart] = s.split(' ');
  const [y, m, day] = datePart.split('-');
  const localDate = new Date(Number(y), Number(m) - 1, Number(day));

  return localDate.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
};

const fmtTime = (d) => {
  const s = normalizeDT(d);
  if (!s) return '—';

  const timePart = s.split(' ')[1];
  if (!timePart) return '—';

  const [hh, mm] = timePart.split(':');
  let hour = Number(hh);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;

  return `${hour.toString().padStart(2, '0')}:${mm} ${ampm}`;
};

const STATUS_COLORS = {
  'Scheduled':   'badge-info',
  'In Progress': 'badge-warning',
  'Completed':   'badge-success',
  'Cancelled':   'badge-danger',
};

const EMPTY = {
  ClientID:'', CaregiverID:'', ServiceID:'',
  StartTime:'', EndTime:'', VisitStatus:'Scheduled', Notes:'',
};

export default function VisitsPage() {
  const { hasRole } = useAuth();
  const canEdit     = hasRole('Admin','CaseManager');

  const [visits,     setVisits]     = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [clients,    setClients]    = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [services,   setServices]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [statusFilt, setStatusFilt] = useState('');
  const [showModal,  setShowModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [deleting,   setDeleting]   = useState(null);
  const [delLoad,    setDelLoad]    = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [vRes, cRes, cgRes, sRes] = await Promise.all([
        visitsAPI.getAll(),
        clientsAPI.getAll(),
        caregiversAPI.getAll(),
        servicesAPI.getAll(),
      ]);
      setVisits(vRes.data.data || []);
      setFiltered(vRes.data.data || []);
      setClients(cRes.data.data || []);
      setCaregivers(cgRes.data.data || []);
      setServices(sRes.data.data || []);
    } catch { toast.error('Failed to load visits.'); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let data = [...visits];
    if (statusFilt) data = data.filter(v => v.VisitStatus === statusFilt);
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(v =>
        (v.ClientName||'').toLowerCase().includes(q) ||
        (v.CaregiverName||'').toLowerCase().includes(q) ||
        (v.ServiceName||'').toLowerCase().includes(q)
      );
    }
    setFiltered(data);
  }, [search, statusFilt, visits]);

  const handleDelete = async () => {
    if (!deleting) return;
    setDelLoad(true);
    try {
      await visitsAPI.delete(deleting.ID);
      toast.success('Visit deleted.');
      setDeleting(null);
      load();
    } catch { toast.error('Delete failed.'); }
    finally   { setDelLoad(false); }
  };

  const counts = {
    total:      visits.length,
    scheduled:  visits.filter(v=>v.VisitStatus==='Scheduled').length,
    inProgress: visits.filter(v=>v.VisitStatus==='In Progress').length,
    completed:  visits.filter(v=>v.VisitStatus==='Completed').length,
    cancelled:  visits.filter(v=>v.VisitStatus==='Cancelled').length,
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Visits</h1>
          <p>{visits.length} total visits</p>
        </div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input className="form-control" placeholder="Search client, caregiver, service…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{paddingLeft:36,minWidth:220}} />
          </div>
          <select className="form-control" style={{width:160}} value={statusFilt}
            onChange={e => setStatusFilt(e.target.value)}>
            <option value="">All Statuses</option>
            {['Scheduled','In Progress','Completed','Cancelled'].map(s =>
              <option key={s} value={s}>{s}</option>
            )}
          </select>
          {canEdit && (
            <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>
              ➕ Schedule Visit
            </button>
          )}
        </div>
      </div>

      {/* Stat chips */}
      <div className="visit-stat-row">
        {[
          {label:'Total',       value:counts.total,      color:'var(--primary)',  bg:'var(--primary-pale)'},
          {label:'Scheduled',   value:counts.scheduled,  color:'var(--info)',     bg:'var(--info-pale)'},
          {label:'In Progress', value:counts.inProgress, color:'var(--warning)',  bg:'var(--warning-pale)'},
          {label:'Completed',   value:counts.completed,  color:'var(--success)',  bg:'var(--success-pale)'},
          {label:'Cancelled',   value:counts.cancelled,  color:'var(--danger)',   bg:'var(--danger-pale)'},
        ].map(s => (
          <div key={s.label} className="visit-stat-chip"
            style={{background:s.bg, cursor:'pointer'}}
            onClick={() => setStatusFilt(s.label === 'Total' ? '' : s.label)}>
            <div style={{fontSize:'1.4rem',fontWeight:700,color:s.color}}>{s.value}</div>
            <div style={{fontSize:'0.72rem',fontWeight:600,color:s.color,opacity:0.8}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="card" style={{padding:0}}>
        {loading ? (
          <div className="loading-state">⏳ Loading visits…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📅</div>
            <div className="empty-state-text">{search||statusFilt ? 'No visits match your filters.' : 'No visits yet.'}</div>
            {!search && !statusFilt && canEdit && (
              <button className="btn btn-primary" style={{marginTop:14}}
                onClick={() => { setEditing(null); setShowModal(true); }}>
                ➕ Schedule First Visit
              </button>
            )}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="ec-table">
              <thead>
                <tr>
                  <th>Client</th><th>Caregiver</th><th>Service</th>
                  <th>Date</th><th>Time</th><th>Status</th><th>Notes</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(v => (
                  <tr key={v.ID}>
                    <td className="bold">{v.ClientName||'—'}</td>
                    <td style={{color:'var(--gray-600)',fontSize:'0.875rem'}}>{v.CaregiverName||'—'}</td>
                    <td><span className="badge badge-primary">{v.ServiceName||'—'}</span></td>
                    <td style={{fontSize:'0.82rem',color:'var(--gray-600)'}}>{fmtDate(v.StartTime)}</td>
                    <td style={{fontSize:'0.78rem',color:'var(--gray-500)'}}>
                      {fmtTime(v.StartTime)}
                      {v.EndTime ? ` – ${fmtTime(v.EndTime)}` : ''}
                    </td>
                    <td><span className={`badge ${STATUS_COLORS[v.VisitStatus]||'badge-gray'}`}>{v.VisitStatus}</span></td>
                    <td style={{fontSize:'0.78rem',color:'var(--gray-500)',maxWidth:160,
                      overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>
                      {v.Notes||'—'}
                    </td>
                    <td>
                      <div style={{display:'flex',gap:6}}>
                        {canEdit && (
                          <>
                            <button className="btn btn-sm btn-secondary" title="Edit"
                              onClick={() => { setEditing(v); setShowModal(true); }}>✏️</button>
                            <button className="btn btn-sm btn-danger" title="Delete"
                              onClick={() => setDeleting(v)}>🗑️</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <VisitFormModal
          visit={editing} clients={clients} caregivers={caregivers} services={services}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}

      {deleting && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header">
              <span className="modal-title">🗑️ Delete Visit</span>
              <button className="modal-close" onClick={() => setDeleting(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{color:'var(--gray-600)'}}>
                Delete visit for <strong>{deleting.ClientName}</strong> on <strong>{fmtDate(deleting.StartTime)}</strong>?
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleting(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={delLoad}>
                {delLoad ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Visit Form Modal ──────────────────────────────────────
function VisitFormModal({ visit, clients, caregivers, services, onClose, onSaved }) {
  const isEdit = !!visit;
  const [form,   setForm]   = useState({
    ClientID:    visit?.ClientID    || '',
    CaregiverID: visit?.CaregiverID || '',
    ServiceID:   visit?.ServiceID   || '',
    StartTime:   toInputDT(visit?.StartTime) || '',
    EndTime:     toInputDT(visit?.EndTime)   || '',
    VisitStatus: visit?.VisitStatus  || 'Scheduled',
    Notes:       visit?.Notes        || '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (f, v) => { setForm(p=>({...p,[f]:v})); if(errors[f]) setErrors(p=>({...p,[f]:''})); };

  const validate = () => {
    const e = {};
    if (!form.ClientID)    e.ClientID    = 'Required';
    if (!form.CaregiverID) e.CaregiverID = 'Required';
    if (!form.StartTime)   e.StartTime   = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await visitsAPI.update(visit.ID, form);
        toast.success('Visit updated.');
      } else {
        await visitsAPI.create(form);
        toast.success('Visit scheduled.');
      }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed.'); }
    finally        { setSaving(false); }
  };

  const Err = ({f}) => errors[f] ? <div style={{color:'var(--danger)',fontSize:'0.75rem',marginTop:3}}>{errors[f]}</div> : null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{isEdit ? '✏️ Edit Visit' : '📅 Schedule New Visit'}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Client <span className="req">*</span></label>
              <select className={`form-control${errors.ClientID?' input-error':''}`}
                value={form.ClientID} onChange={e => set('ClientID', e.target.value)}>
                <option value="">— Select Client —</option>
                {clients.map(c => <option key={c.ID} value={c.ID}>{c.FirstName} {c.LastName}</option>)}
              </select>
              <Err f="ClientID" />
            </div>
            <div className="form-group">
              <label className="form-label">Caregiver <span className="req">*</span></label>
              <select className={`form-control${errors.CaregiverID?' input-error':''}`}
                value={form.CaregiverID} onChange={e => set('CaregiverID', e.target.value)}>
                <option value="">— Select Caregiver —</option>
                {caregivers.filter(c => c.IsActive || c.IsCaregiverActive).map(c =>
                  <option key={c.UserID} value={c.UserID}>{c.FirstName} {c.LastName}</option>
                )}
              </select>
              <Err f="CaregiverID" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Service</label>
              <select className="form-control" value={form.ServiceID} onChange={e => set('ServiceID', e.target.value)}>
                <option value="">— Select Service —</option>
                {services.map(s => <option key={s.ID} value={s.ID}>{s.ServiceName}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={form.VisitStatus} onChange={e => set('VisitStatus', e.target.value)}>
                {['Scheduled','In Progress','Completed','Cancelled'].map(s =>
                  <option key={s} value={s}>{s}</option>
                )}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Start Time <span className="req">*</span></label>
              <input className={`form-control${errors.StartTime?' input-error':''}`}
                type="datetime-local" value={form.StartTime} onChange={e => set('StartTime', e.target.value)} />
              <Err f="StartTime" />
            </div>
            <div className="form-group">
              <label className="form-label">End Time</label>
              <input className="form-control" type="datetime-local"
                value={form.EndTime} onChange={e => set('EndTime', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={3} value={form.Notes}
              onChange={e => set('Notes', e.target.value)} placeholder="Optional visit notes…" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? '💾 Update Visit' : '📅 Schedule Visit'}
          </button>
        </div>
      </div>
    </div>
  );
}